import React from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeOptionalText } from '../lib/sanitize';

// ====== ストレージユーティリティ ======
const STORAGE_KEY = 'pachicalc:runs';
const MAX_HISTORY = 50;

function loadHistory() {
  const CANDIDATE_KEYS = ['pachicalc:runs', 'runs', 'pachicalc_runs'];
  let raw = null;
  for (const k of CANDIDATE_KEYS) {
    const v = localStorage.getItem(k);
    if (v) {
      raw = v;
      break;
    }
  }
  if (!raw) return [];

  let arr;
  try {
    arr = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(arr)) return [];

  // 保存形 → 表示形へマッピング
  const normalize = (r) => {
    const modeLabel =
      r.mode === 'simple'
        ? '簡易'
        : r.mode === 'detail'
          ? '詳細'
          : r.mode === '簡易' || r.mode === '詳細'
            ? r.mode
            : '簡易';

    // createdAt が無い保存も想定して補完
    const createdAt = r.createdAt || r.time || new Date().toISOString();

    // id が無ければ生成（衝突回避で一応ランダム付与）
    const id = r.id || Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

    // 型を安全に
    const num = (x) => (x === '' || x === null || x === undefined ? null : Number(x));
    const text = (x) => sanitizeOptionalText(x ?? '');

    return {
      id,
      mode: modeLabel,
      createdAt,
      ratePerK: num(r.ratePerK ?? r.rotationRate) ?? 0,
      normalSpins: num(r.normalSpins),
      chanceSpins: num(r.chanceSpins),
      payout: num(r.payout ?? r.totalOutput),
      diffBalls: num(r.diffBalls ?? r.finalDifference),
      holdDecreaseRate: r.holdDecreaseRate ?? r.decreaseRate ?? null,
      lendBalls: num(r.lendBalls ?? r.ballsPer1000),
      storeName: text(r.storeName),
      machineName: text(r.machineName),
    };
  };

  try {
    const mapped = arr.map(normalize);
    // 新しい順で統一
    return mapped.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
  } catch {
    return [];
  }
}

function saveHistory(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

// ====== 表示ユーティリティ ======
function formatDate(iso) {
  const dt = new Date(iso);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

function includesQ(item, q) {
  if (!q) return true;
  const text = [
    item.mode,
    item.ratePerK?.toString(),
    item.normalSpins?.toString(),
    item.chanceSpins?.toString(),
    item.payout?.toString(),
    item.diffBalls?.toString(),
    item.holdDecreaseRate?.toString(),
    item.lendBalls?.toString(),
    item.storeName,
    item.machineName,
    formatDate(item.createdAt),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return text.includes(q.toLowerCase());
}

function sortList(list, key) {
  const cp = [...list];
  switch (key) {
    case 'new':
      cp.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
      break;
    case 'old':
      cp.sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
      break;
    case 'rateDesc':
      cp.sort((a, b) => (b.ratePerK ?? 0) - (a.ratePerK ?? 0));
      break;
    case 'rateAsc':
      cp.sort((a, b) => (a.ratePerK ?? 0) - (b.ratePerK ?? 0));
      break;
    default:
      break;
  }
  return cp;
}

// ====== CSV ======
function toCSV(rows) {
  const header = [
    'id',
    'mode',
    'storeName',
    'machineName',
    'createdAt',
    'ratePerK',
    'normalSpins',
    'chanceSpins',
    'payout',
    'diffBalls',
    'holdDecreaseRate',
    'lendBalls',
  ];
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    let s = String(v);
    // eslint-disable-next-line no-control-regex
    s = s.replace(/[\u0000-\u001F\u007F]/g, '');
    if (/^[=+\-@]/.test(s)) {
      s = `'${s}`;
    }
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    header.join(','),
    ...rows.map((r) =>
      [
        r.id,
        r.mode,
        r.storeName || '',
        r.machineName || '',
        r.createdAt,
        r.ratePerK,
        r.normalSpins ?? '',
        r.chanceSpins ?? '',
        r.payout ?? '',
        r.diffBalls ?? '',
        r.holdDecreaseRate ?? '',
        r.lendBalls ?? '',
      ]
        .map(esc)
        .join(',')
    ),
  ];
  return lines.join('\n');
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ====== 小さな部品 ======
function Chip({ children, color = 'bg-blue-100 text-blue-800' }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      {children}
    </span>
  );
}

function KeyVal({ label, value }) {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;
  return (
    <>
      <div className="text-gray-500">{label}</div>
      <div className="col-span-2">{displayValue}</div>
    </>
  );
}

// ====== カード ======
function HistoryCard({ item, onRestore, onDelete }) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="rounded-2xl shadow-sm border border-gray-200 p-3 mb-3 bg-white">
      {/* KPI行 */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <Chip>{item.mode}</Chip>
          <div className="text-xs text-gray-500">{formatDate(item.createdAt)}</div>
        </div>
        <div className="text-lg font-bold tracking-tight">
          回転率 <span className="text-blue-700">{item.ratePerK.toFixed(2)}</span> 回/K
        </div>
      </div>

      {/* サマリー */}
      <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2 text-[13px]">
        <KeyVal label="通常回転" value={item.normalSpins ?? '-'} />
        <KeyVal label="最終差玉" value={item.diffBalls ?? '-'} />
        <KeyVal label="獲得出玉" value={item.payout ?? '-'} />
      </div>

      <div className="mt-3 space-y-1 text-[13px]">
        <div>
          <div className="text-gray-500">店舗名</div>
          <div>{item.storeName ? item.storeName : '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">機種名</div>
          <div>{item.machineName ? item.machineName : '-'}</div>
        </div>
      </div>

      {/* 補助：開閉 */}
      <button className="mt-2 text-xs text-blue-600 underline" onClick={() => setOpen((v) => !v)}>
        {open ? '詳細を閉じる' : '詳細を表示'}
      </button>
      {open && (
        <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2 text-[13px]">
          <KeyVal label="チャンス中" value={item.chanceSpins ?? '-'} />
          <KeyVal label="持ち球減少率" value={item.holdDecreaseRate ?? '-'} />
          <KeyVal label="貸玉数" value={item.lendBalls ?? '-'} />
        </div>
      )}

      {/* アクション */}
      <div className="flex justify-end gap-2 mt-3">
        <button
          onClick={() => onRestore(item.id)}
          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm active:scale-[0.98]"
        >
          復元
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-sm border border-red-200"
        >
          削除
        </button>
      </div>
    </div>
  );
}

// ====== ヘッダ ======
function HistoryHeader({ count, q, setQ, sort, setSort, onExport, onClearAll }) {
  return (
    <div className="sticky top-0 z-10 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b">
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-semibold">履歴</div>
          <span className="text-xs text-gray-500">
            全{count}件（最大{MAX_HISTORY}件）
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            aria-label="Export"
            onClick={onExport}
            title="CSVエクスポート"
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            ⤓
          </button>
          <button
            aria-label="Clear All"
            onClick={onClearAll}
            title="全削除"
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            🗑
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-xs border rounded-lg px-2 py-1"
          >
            <option value="new">新しい順</option>
            <option value="old">古い順</option>
            <option value="rateDesc">回転率 高→低</option>
            <option value="rateAsc">回転率 低→高</option>
          </select>
        </div>
      </div>
      <div className="px-3 pb-2">
        <input
          type="search"
          placeholder="通常回転数・差玉・出玉などで検索"
          className="w-full text-sm border rounded-xl px-3 py-2"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
    </div>
  );
}

// ====== ページ本体 ======
export default function HistoryPage() {
  const navigate = useNavigate();
  const [list, setList] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [sort, setSort] = React.useState('new');

  React.useEffect(() => {
    const loaded = loadHistory();
    setList(loaded.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)));
  }, []);

  const handleRestore = (id) => {
    const target = list.find((x) => x.id === id);
    if (!target) return;

    // カードの表示モード（"簡易" | "詳細"）からフォーム用モードへ
    const formMode = target.mode === '詳細' ? 'detail' : 'simple';

    let restore;
    if (formMode === 'simple') {
      // 簡易フォームが受け取るトップレベル項目
      restore = {
        mode: 'simple',
        normalSpins: target.normalSpins ?? '',
        totalOutput: target.payout ?? '',
        finalDifference: target.diffBalls ?? '',
        storeName: target.storeName ?? '',
        machineName: target.machineName ?? '',
      };
    } else {
      // 詳細フォームが受け取るトップレベル項目（想定：保存時のキーに合わせる）
      restore = {
        mode: 'detail',
        normalSpins: target.normalSpins ?? '',
        chanceSpins: target.chanceSpins ?? '',
        decreaseRate: target.holdDecreaseRate ?? '',
        ballsPer1000: target.lendBalls ?? '',
        totalOutput: target.payout ?? '',
        finalDifference: target.diffBalls ?? '',
        storeName: target.storeName ?? '',
        machineName: target.machineName ?? '',
      };
    }

    navigate('/', { replace: true, state: { restore } });
  };

  const handleDelete = (id) => {
    const next = list.filter((x) => x.id !== id);
    setList(next);
    saveHistory(next);
  };

  const handleClearAll = () => {
    if (!window.confirm('履歴をすべて削除します。よろしいですか？')) return;
    setList([]);
    saveHistory([]);
  };

  const handleExport = () => {
    const filtered = sortList(
      list.filter((x) => includesQ(x, q)),
      sort
    );
    const csv = toCSV(filtered);
    downloadText(`pachicalc_history_${Date.now()}.csv`, csv);
  };

  const view = sortList(
    list.filter((x) => includesQ(x, q)),
    sort
  );

  return (
    <div className="max-w-3xl mx-auto">
      <HistoryHeader
        count={list.length}
        q={q}
        setQ={setQ}
        sort={sort}
        setSort={setSort}
        onExport={handleExport}
        onClearAll={handleClearAll}
      />
      <div className="p-3">
        {view.length === 0 ? (
          <div className="text-sm text-gray-500 p-6 text-center border rounded-2xl">
            履歴がありません。計算後に「保存する」を押すとここに表示されます。
          </div>
        ) : (
          view.map((item) => (
            <HistoryCard
              key={item.id}
              item={item}
              onRestore={handleRestore}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
      <div style={{ height: 'calc(72px + env(safe-area-inset-bottom))' }} />
    </div>
  );
}

export { toCSV };
